// --- React libraries ---
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Sparkles, Pin } from 'lucide-react';

// --- shared components ---
import Portal from './ui/Portal';
import { NAV_SECTIONS_MAP } from './navigationSections';
import type { NavSection } from './navigationSections';

// --- constants ---
import { NAV_ITEMS } from '../constants';

interface NavigationPopoverProps {
  isOpen: boolean;
  sectionId: string | null;
  anchorRect: DOMRect | null;
  onClose: () => void;
  onNavigate: (id: string) => void;
  currentPath: string;
  onToggleAssistant: () => void;
  onPopoverEnter: () => void;
  onPopoverLeave: () => void;
}

const NavigationPopover: React.FC<NavigationPopoverProps> = ({
  isOpen,
  sectionId,
  anchorRect,
  onClose,
  onNavigate,
  currentPath,
  onToggleAssistant,
  onPopoverEnter,
  onPopoverLeave
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);

  // Pin/Unpin state management with localStorage persistence
  const [pinnedItems, setPinnedItems] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('grid-pinned-items');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      // Failed to load pinned items from localStorage - using empty set
      return new Set();
    }
  });

  // Persist pinned items to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('grid-pinned-items', JSON.stringify(Array.from(pinnedItems)));
      // Dispatch custom event to notify other components
      window.dispatchEvent(new Event('pinnedItemsChanged'));
    } catch {
      // Failed to save pinned items to localStorage
    }
  }, [pinnedItems]);

  const togglePin = (itemId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent navigation
    setPinnedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Resolve section - handle "pinned" section dynamically
  const section = useMemo<NavSection | null>(() => {
    if (!sectionId) return null;

    if (sectionId === 'pinned') {
      // Create virtual pinned section
      const pinnedNavItems = NAV_ITEMS.filter(item => pinnedItems.has(item.id));
      return {
        id: 'pinned',
        label: 'Pinned',
        items: pinnedNavItems,
        placement: 'main' as const,
        icon: Pin
      };
    }

    return NAV_SECTIONS_MAP[sectionId] || null;
  }, [sectionId, pinnedItems]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (popoverRef.current?.contains(target)) return;
      if (target.closest('[data-nav-overlay-trigger]')) return;
      onClose();
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, onClose]);

  const panelStyle = useMemo(() => {
    const sectionType = section?.id;
    const panelWidth = sectionType === 'help' ? 600 : 520;
    const panelHeight = sectionType === 'help' ? 480 : 420;
    const gutter = 16;
    const defaultLeft = 240;
    const defaultTop = 72;

    if (!anchorRect || typeof window === 'undefined') {
      return {
        top: defaultTop,
        left: defaultLeft,
        width: panelWidth
      };
    }

    const candidateLeft = anchorRect.right + gutter;
    const maxLeft = window.innerWidth - panelWidth - gutter;
    const left = Math.min(candidateLeft, Math.max(gutter, maxLeft));

    // Align popover top with anchor top (right next to the title)
    const candidateTop = anchorRect.top;
    const maxTop = window.innerHeight - panelHeight - gutter;
    const top = Math.min(Math.max(gutter, candidateTop), Math.max(gutter, maxTop));

    return { top, left, width: panelWidth };
  }, [anchorRect, section]);

  if (!isOpen || !section) return null;

  const sectionItems = section.items ?? [];
  return (
    <Portal>
      <div className="fixed inset-0 z-[80] pointer-events-none">
        <div
          ref={popoverRef}
          className="pointer-events-auto absolute rounded-3xl border border-border bg-card backdrop-blur-sm shadow-[0_25px_80px_rgba(2,6,23,0.55)] min-w-[320px] max-w-[560px]"
          style={panelStyle}
          onMouseEnter={onPopoverEnter}
          onMouseLeave={onPopoverLeave}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-border/60">
            <div>
              <p className="text-[10px] uppercase tracking-[0.4em] text-muted-foreground/80 mb-1">Blueprints</p>
              <h3 className="text-lg font-semibold text-foreground">{section.label}</h3>
            </div>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-accent text-muted-foreground" aria-label="Close navigation popover">
              <X size={18} />
            </button>
          </div>

          {section.id === 'ai' ? (
            <div className="flex flex-col items-center justify-center text-center py-10 px-6">
              <div className="w-4/5 h-px bg-border/40 mb-6" aria-hidden="true" />
              <div className="w-16 h-16 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-5">
                <Sparkles size={28} />
              </div>
              <h4 className="text-xl font-semibold mb-6">Summon the SWM Assistant</h4>
              <button
                onClick={() => {
                  onToggleAssistant();
                  onClose();
                }}
                className="px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors"
              >
                Open Assistant
              </button>
            </div>
          ) : section.id === 'help' ? (
            <div className="px-6 py-6 space-y-4">
              <p className="text-xs uppercase tracking-[0.4em] text-muted-foreground">Info Center</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {sectionItems
                  .filter(item => ['help-hub', 'contact-support'].includes(item.id))
                  .map(item => (
                    <button
                      key={item.id}
                      onClick={() => {
                        onNavigate(item.id);
                        onClose();
                      }}
                      className={`text-left p-5 rounded-3xl border bg-card/70 transition-colors ${
                        currentPath === item.id
                          ? 'border-primary/60 bg-primary/10 text-primary'
                          : 'border-border/60 hover:border-primary/40 hover:bg-accent/40 text-foreground'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 rounded-2xl bg-accent/50 flex items-center justify-center">
                          <item.icon size={20} className="text-muted-foreground" />
                        </div>
                        <div>
                          <p className="text-base font-semibold leading-tight">{item.label}</p>
                          <p className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground/80">
                            {item.id === 'contact-support' ? 'Submit Ticket' : 'Help Center'}
                          </p>
                        </div>
                      </div>
                      <p className="text-sm text-foreground/70">
                        {item.id === 'contact-support'
                          ? 'Open a ticket and we will route it to the right team.'
                          : 'Browse articles, templates, and onboarding guides.'}
                      </p>
                    </button>
                  ))}
              </div>
            </div>
          ) : (
            <div className="px-6 py-4 max-h-[360px] overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-3">
              {sectionItems.length ? sectionItems.map(item => {
                const isPinned = pinnedItems.has(item.id);
                const isPinnedSection = section?.id === 'pinned';
                return (
                  <div
                    key={item.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      onNavigate(item.id);
                      onClose();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onNavigate(item.id);
                        onClose();
                      }
                    }}
                    className={`text-left p-4 rounded-2xl border transition-all duration-200 group relative cursor-pointer ${
                      currentPath === item.id
                        ? 'border-primary/60 bg-primary/10 text-primary'
                        : 'border-border/60 hover:border-primary/40 hover:bg-accent/40 text-foreground'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold">{item.label}</span>
                      <div className="flex items-center gap-2">
                        {/* Show unpin button in pinned section, pin/unpin in other sections */}
                        {isPinnedSection ? (
                          <button
                            onClick={(e) => togglePin(item.id, e)}
                            className="p-1 rounded-md transition-all text-primary bg-primary/10 hover:bg-primary/20"
                            aria-label="Unpin item"
                            title="Unpin"
                          >
                            <Pin size={14} className="fill-current" />
                          </button>
                        ) : (
                          <button
                            onClick={(e) => togglePin(item.id, e)}
                            className={`p-1 rounded-md transition-all ${
                              isPinned
                                ? 'text-primary bg-primary/10'
                                : 'text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-foreground hover:bg-accent/50'
                            }`}
                            aria-label={isPinned ? 'Unpin item' : 'Pin item'}
                            title={isPinned ? 'Unpin' : 'Pin'}
                          >
                            <Pin size={14} className={isPinned ? 'fill-current' : ''} />
                          </button>
                        )}
                        <item.icon size={18} className="text-muted-foreground" />
                      </div>
                    </div>
                  </div>
                );
              }) : (
                <div className="col-span-full flex flex-col items-center justify-center py-16 text-center text-muted-foreground">
                  <p className="text-sm font-medium">
                    {section?.id === 'pinned'
                      ? 'No pinned items yet.'
                      : 'No pages have been created for this section yet.'}
                  </p>
                  <p className="text-xs mt-2 opacity-70">
                    {section?.id === 'pinned'
                      ? 'Pin items from other sections to see them here.'
                      : 'Select another tab to continue exploring.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
};

export default NavigationPopover;
